import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";
import { Routes } from "router/routes";
import { howItWorks1, howItWorks2, howItWorks3 } from "assets/website";
import { HowItWorksFaqSection } from "./faq";
import { VideoModal } from "../VideoModal";

/* ─── Steps data ─── */
interface Step {
  number: number;
  title: string;
  description: string;
  image: string;
  imagePosition: "left" | "right";
}

const steps: Step[] = [
  {
    number: 1,
    title: "AI CV Screening and Assessment Creation",
    description:
      "The Hiring Assistant goes beyond keywords to understand experience depth, role progression, and real exposure to skills. It automatically generates role-specific assessments questions and scenarios specific to what the job actually demands, not what the CV claims.",
    image: howItWorks1,
    imagePosition: "right",
  },
  {
    number: 2,
    title: "Candidate Assessment Quiz",
    description:
      "Candidates take a short, structured assessment designed to reveal how they think, reason, and apply knowledge. The goal is to see how they approach problems, where they're strong, and where they struggle.",
    image: howItWorks2,
    imagePosition: "left",
  },
  {
    number: 3,
    title: "Talent Management & Report",
    description:
      "Once assessments are completed, Precision translates raw responses into clear, decision-ready insights. You get structured reports that highlight capability gaps, strengths, risk areas, and overall role fit.",
    image: howItWorks3,
    imagePosition: "right",
  },
];

/* ─── Sparkle decorator ─── */
const Sparkle = () => (
  <div className="flex justify-center py-6">
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
      <polygon
        points="16,0 17.6,10.2 24,2.1 20.2,11.8 29.9,8 21.8,14.4 32,16 21.8,17.6 29.9,24 20.2,20.2 24,29.9 17.6,21.8 16,32 14.4,21.8 8,29.9 11.8,20.2 2.1,24 10.2,17.6 0,16 10.2,14.4 2.1,8 11.8,11.8 8,2.1 14.4,10.2"
        fill="#C6E0FF"
      />
    </svg>
  </div>
);

/* ─── Component ─── */
const HowItWorksUI = () => {
  const navigate = useNavigate();
  const [showVideo, setShowVideo] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    AOS.init({ duration: 1000, offset: 100, easing: "ease-in-out", once: true });
    AOS.refresh();
  }, []);

  const handleTalkToTeam = () => {
    window.open("https://calendly.com/precisionmatch/product-demo", "_blank", "noopener,noreferrer");
  };

  return (
    <div className="bg-[#F5F8FF] overflow-x-hidden">
      {/* ────── Hero ────── */}
      <section className="pt-36 pb-8 md:pt-44 md:pb-12">
        <div className="max-w-[1000px] mx-auto lg:px-8 px-6 text-center">
          <h1
            data-aos="fade-up"
            className="text-[28px] md:text-5xl lg:text-6xl font-display font-semibold text-[#00003F] lg:leading-[58px] md:leading-[40px] leading-[30px] mb-6"
          >
            An Efficient Path from Job Description to the Right Hire
          </h1>

          <button
            data-aos="fade-up"
            data-aos-delay="100"
            onClick={() => setShowVideo(true)}
            className="px-10 py-4 text-base bg-[#012169] hover:bg-[#011550] text-white font-semibold rounded-lg transition-all duration-200 hover:shadow-lg"
          >
            Experience Precision Live
          </button>
        </div>
      </section>

      {/* ────── Steps ────── */}
      <section className="pb-12 md:pb-20 px-4 lg:px-6">
        <div className="max-w-[1216px] mx-auto">
          {steps.map((step, index) => (
            <div key={step.number}>
              {/* Sparkle divider between steps */}
              {index > 0 && <Sparkle />}

              {/* Step card */}
              <div
                data-aos="fade-up"
                data-aos-delay={index * 100}
                className="bg-white rounded-2xl md:mt-0 sm:mt-[230px] mt-[100px] rounded-2xl p-6 pt-[100px] md:pt-10 md:p-10 overflow-visible"
              >
                <div
                  className={`flex flex-col ${
                    step.imagePosition === "left"
                      ? "md:flex-row"
                      : "md:flex-row-reverse"
                  } gap-8 md:gap-12 items-center`}
                >
                  {/* Image */}
                  <div
                    className={`w-full md:w-1/2 flex-shrink-0 -mt-[180px] sm:-mt-[300px] md:mt-0 ${
                      step.imagePosition === "left"
                        ? "min-[1390px]:-ml-[100px]"
                        : "min-[1390px]:-mr-[100px]"
                    }`}
                  >
                    <div
                      className="bg-[#C6E0FF] rounded-2xl p-6"
                      style={{ boxShadow: `${step.imagePosition === "left" ? "-32.4px" : "32.4px"} 32.4px 24.3px 20.25px #0000001A` }}
                    >
                      <img
                        src={step.image}
                        alt={step.title}
                        className="w-full rounded-xl"
                      />
                    </div>
                  </div>

                  {/* Text */}
                  <div className="w-full md:w-1/2">
                    <span className="text-3xl sm:text-5xl md:text-6xl font-bold text-[#000B55] block mb-1">
                      {step.number}.
                    </span>
                    <h3 className="text-xl md:text-2xl font-bold font-display text-[#050B17] mb-3">
                      {step.title}
                    </h3>
                    <p className="text-base text-[#475467] leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-12" data-aos="fade-up">
          <button
            onClick={() => setShowVideo(true)}
            className="px-10 py-4 text-base bg-[#012169] hover:bg-[#011550] text-white font-semibold rounded-lg transition-all duration-200 hover:shadow-lg"
          >
            Experience Precision Live
          </button>
          <p className="text-sm text-neutral-500 mt-3">
            Have a feel of Precision before Signing Up
          </p>
        </div>
      </section>

      {/* ────── FAQ ────── */}
      <HowItWorksFaqSection />

      <VideoModal show={showVideo} onClose={() => setShowVideo(false)} />
    </div>
  );
};

export { HowItWorksUI };
